import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {FormBuilder, Validators} from "@angular/forms";
import {AppUtils} from "../../../../core/config/app.utils";
import {NzModalRef} from "ng-zorro-antd/modal";
import {ItineraryService} from "../../../../core/services/itinerary.service";
import {ItineraryTemplatesService} from "../../../../core/services/itinerary-templates.service";
import {GooglePlacesService} from "../../../../core/services/google-places.service";
import {BehaviorSubject, Subscription} from "rxjs";
import {distinctUntilChanged} from "rxjs/operators";
import {SETTINGS} from "../../../../core/config/common.settings";
import {NzMessageService} from "ng-zorro-antd/message";
import {GoogleMatrixService} from "../../../../core/services/google-matrix.service";
import {amountFormatter, amountParser, currencyFormatter} from "../../../../core/ult-functions/currency-calculation";
import { TranslocoService } from '@ngneat/transloco';
import { withItineraryLockSession } from '../../../../core/ult-functions/itinerary-tab-session';

@Component({
  selector: "app-add-edit-transport-content",
  templateUrl: "./add-edit-transport-content.component.html",
  styleUrls: ["./add-edit-transport-content.component.scss"]
})
export class AddEditTransportContentComponent implements OnInit, OnDestroy {
  @Input() itinerary?: any;
  @Input() mode?: any;
  @Input() transportIndex?: any;
  @Input() dayIndex?: any;
  @Input() country?: string;
  @Input() transportConfig?: any;
  @Input() itineraryMode: 'itinerary' | 'template' = 'itinerary';
  @Input() sessionId?: string;

  transportForm: any;
  formErrors: any;
  transportTypes = SETTINGS.TRANSPORT_TYPES;

  // for google api
  googleSuggestions: any;
  googlePlaceDetailsFrom: any;
  googlePlaceDetailsFromTo: any;

  // search
  optionListFrom: any = [];
  optionListTo: any = [];
  selectedTransportFrom?: any;
  selectedTransportTo?: any;
  isLoading = false;
  searchChangeFrom$ = new BehaviorSubject({});
  searchChangeTo$ = new BehaviorSubject({});
  selectedIndexDate: any;

  currentCountryBoxChecked = false;
  countyData: any;
  isBothDestinationSelected = new BehaviorSubject(false);
  autoMileage = 0;
  currencies: any[] = [];
  isPrimaryCurrency = true;
  costPerMileage = 0;
  filteredTransportValues: any[] = [];
  filteredAltTransportValues: any[] = [];
  isButtonLoading = false;
  protected readonly SETTINGS = SETTINGS;
  mileageUnitOptions: any[] = [];
  private langChangeSub?: Subscription;
  selectedDayIndex: any;

  get activeService(): ItineraryService | ItineraryTemplatesService {
    return this.itineraryMode === 'template' ? this.itineraryTemplatesService : this.itineraryService;
  }

  constructor(private fb: FormBuilder,
              private modal: NzModalRef,
              private message: NzMessageService,
              private itineraryService: ItineraryService,
              private itineraryTemplatesService: ItineraryTemplatesService,
              private googlePlacesService: GooglePlacesService,
              private googleMatrixService: GoogleMatrixService,
              private translocoService: TranslocoService) {
    this.formErrors = {
      selectedDate: {},
      typeOfVehicle: {},
      totalCost: {},
      mileage: {},
      costPerMileage: {},
      notes: {},
      isMileageAuto : {}
    };
  }


  private getMileageUnitOptions(): Array<{ value: string; label: string }> {
    const lang = this.translocoService.getActiveLang();
    const filtered = SETTINGS.MILEAGE_UNIT.filter(item => item.language === lang);
    return (filtered.length ? filtered : SETTINGS.MILEAGE_UNIT.filter(item => item.language === 'en'))
      .map(({ value, label }) => ({ value, label }));
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }
  ngOnInit(): void {
    this.mileageUnitOptions = this.getMileageUnitOptions();
    this.langChangeSub = this.translocoService.langChanges$.subscribe(() => {
      this.mileageUnitOptions = this.getMileageUnitOptions();
    });
    this.addCurrency();
    this.transportForm = this.fb.group({
      selectedDate: ["", [Validators.required]],
      typeOfVehicle: ["land", [Validators.required]],
      totalCost: [0],
      mileage: [{value: 0 , disabled: true }],
      driverBata: [0],
      perAdult: [0],
      perChild: [0],
      perInfant: [0],
      mileageUnit: ["km", [Validators.required]],
      hours: [0, [Validators.required]],
      minutes: [0, [Validators.required]],
      costPerMileage: [0],
      transVehicleConfig: [""],
      vehicleImage : [''],
      notes: [""],
      isMileageAuto : [true]
    });

    this.isBothDestinationSelected.subscribe(data => {
      if (data) {
        this.getTimeTaken();
      }
    });

    if (this.mode === SETTINGS.ITINERARY_CONTENT_MODE.NEW) {
      this.filteredTransportValues = this.getTransportConfig(true);
      console.log(this.filteredTransportValues);
      this.filteredAltTransportValues = this.getAltConfigs().filter((alt: any) => !this.filteredTransportValues.some((f: any) => f._id === alt._id));
      let previousDateBata = 0;
      if (this.itinerary.days[0].transport.length !== 0){
        previousDateBata = this.itinerary.days[0].transport[0].driverBata;
      }
      const date = {
        selectedDate: this.itinerary.days[this.dayIndex].date,
        driverBata: previousDateBata
      };
      this.transportForm.patchValue(date);
    }

    if (this.mode === SETTINGS.ITINERARY_CONTENT_MODE.EDIT) {
      const transport = this.itinerary.days[this.dayIndex].transport[this.transportIndex];
      this.isPrimaryCurrency = transport?.isPrimaryCurrency ?? true;
      this.autoMileage = transport?.autoMileage ?? 0;
      this.selectedDayIndex = this.dayIndex;
      this.transportForm.patchValue(transport, {emitEvent: true});
      this.mileageFormVaildityUpdateOnEdit(transport?.isMileageAuto ?? true);
      if (transport.typeOfVehicle === 'land') {
        this.filteredTransportValues = this.getTransportConfig(false);
        this.filteredAltTransportValues = this.getAltConfigs().filter((alt: any) => !this.filteredTransportValues.some((f: any) => f._id === alt._id));
      } else {
        this.filteredTransportValues = [];
        this.filteredAltTransportValues = [];
      }
    }

    this.transportForm.valueChanges.subscribe((form: any) => {
      let calTotalCost = 0;
      if (form.typeOfVehicle === 'land'){
        const {mileage} = this.transportForm.getRawValue();
        const tempMileage = mileage ? mileage : 0;
        const tempCostPerMileage = form.costPerMileage ? form.costPerMileage : 0;
        const tempDriverBata = form.driverBata ? form.driverBata : 0;
        calTotalCost = (tempMileage * tempCostPerMileage) + tempDriverBata;
        this.transportForm.patchValue({totalCost: calTotalCost}, { emitEvent: false });
      }

      if (form.typeOfVehicle !== 'land'){
        const {pax} = this.itinerary.tour;
        const totalCostPerAdult = form.perAdult * pax.adults;
        const totalCostPerChild = form.perChild * pax.children;
        const totalCostPerInfant = form.perInfant * pax.infants;
        calTotalCost = totalCostPerAdult + totalCostPerChild + totalCostPerInfant;
        this.transportForm.patchValue({totalCost: calTotalCost}, { emitEvent: false });
      }

      this.formErrors = AppUtils.getFormErrors(this.transportForm, this.formErrors);
    });

    this.transportForm.get("typeOfVehicle")?.valueChanges.pipe(distinctUntilChanged()).subscribe((value: string) => {
      if (value === 'land') {
        this.filteredTransportValues = this.getTransportConfig(true);
        this.filteredAltTransportValues = this.getAltConfigs().filter((alt: any) => !this.filteredTransportValues.some((f: any) => f._id === alt._id));
      } else {
        this.filteredTransportValues = [];
        this.filteredAltTransportValues = [];
        this.transportForm.patchValue({ costPerMileage: 0, vehicleImage: '', transVehicleConfig: '' }, { emitEvent: false });
      }
    });

    this.transportForm.get("isMileageAuto").valueChanges.subscribe((data: any) => {
      this.mileageFormVaildityUpdateForms(data);
    });

    this.transportForm.get("mileageUnit").valueChanges.subscribe((data: any) => {
      if (this.transportForm.get("mileageUnit")?.touched) {
          const mileage = this.convertDistance(this.transportForm.get("mileage").value, data);
          this.transportForm.patchValue({mileage});
      }
    });
  }

  async selectedTransportLocation(data: any): Promise<any> {
    this.isLoading = true;
    try {
      const detailsData = await this.googlePlacesService.getPlaceDetails(data);
      return detailsData.location;
      this.isLoading = false;
    } catch (e) {
      this.message.error(this.translocoService.translate('itineraries.transport.messages.somethingWentWrong'));
    }
  }

  // async selectedDestinationTo(data: any): Promise<any> {
  //   try {
  //     this.googlePlaceDetailsFromTo = await this.googlePlacesService.getPlaceDetails(data.placeId);
  //   } catch (e) {
  //     console.log(e);
  //     this.message.error("Something Went Wrong");
  //   }
  // }
  //
  // onSearchFrom(value: string): void {
  //   this.isLoading = true;
  //   this.searchChangeFrom$.next(value);
  // }
  //
  // onSearchTo(value: string): void {
  //   this.isLoading = true;
  //   this.searchChangeTo$.next(value);
  // }


  selectedDestinationFrom(data: any): void {
    if (this.selectedTransportTo !== undefined && this.selectedTransportFrom !== undefined) {
      this.isBothDestinationSelected.next(true);
    }
  }

  selectedDestinationTo(data: any): void {
    if (this.selectedTransportTo !== undefined && this.selectedTransportFrom !== undefined) {
      this.isBothDestinationSelected.next(true);
    }
  }

  searchTransportFrom(value: string): void {
    if (value !== "") {
      const payload = this.getPayload(this.currentCountryBoxChecked, value);
      this.googlePlacesService.postAutoComplete({...payload}).then(res => {

        this.optionListFrom = res.suggestions;
      });
    }
  }

  searchTransportTo(value: string): void {
    if (value !== "") {
      const payload = this.getPayload(this.currentCountryBoxChecked, value);
      this.googlePlacesService.postAutoComplete({...payload}).then(res => {

        this.optionListTo = res.suggestions;
      });
    }
  }

  getPayload(isRestrict: boolean, value: string): any {
    if (isRestrict && this.countyData) {
      return {
        input: value,
        locationRestriction: {
          rectangle: {
            high: {
              latitude: this.countyData?.viewport?.high.latitude,
              longitude: this.countyData?.viewport?.high.longitude
            },
            low: {
              latitude: this.countyData?.viewport?.low.latitude,
              longitude: this.countyData?.viewport?.low.longitude
            }
          }
        },
        primaryTypes: ["airport", "subway_station", "bus_station", "train_station", "locality"]
      };
    } else {
      return {
        input: value,
        primaryTypes: []
      };
    }
  }

  onChangeCurrentCountry(value: any): void {
    this.currentCountryBoxChecked = value;

    if (this.currentCountryBoxChecked) {
      this.isLoading = true;
      this.getCurrentLocation().then(r => {
        this.countyData = r;
        this.isLoading = false;
      });
    }
  }

  handleCancel(): void {
    this.modal.close();
  }

  async addTransport(): Promise<any> {
    this.isButtonLoading = true;
    try {
      this.selectedIndexDate = this.itinerary.days.map((e: { date: Date; }) =>
        e.date).indexOf(this.transportForm.get("selectedDate").value);

      const formData = this.transportForm.getRawValue();

      formData.mileage = formData.mileage !== "" ? formData.mileage : 0;
      formData.costPerMileage = formData.costPerMileage !== "" ? formData.costPerMileage : 0;
      formData.driverBata = formData.driverBata !== "" ? formData.driverBata : 0;

      const locationFrom = await this.selectedTransportLocation(this.selectedTransportFrom.placeId);
      const locationTo = await this.selectedTransportLocation(this.selectedTransportTo.placeId);

      const payload = {
        ...formData,
        from: this.selectedTransportFrom.placeId,
        to: this.selectedTransportTo.placeId,
        selectedDateIndex: this.selectedIndexDate,
        displayNameTo: this.selectedTransportTo.structuredFormat.mainText.text,
        displayNameFrom: this.selectedTransportFrom.structuredFormat.mainText.text,
        locationFrom,
        locationTo,
        autoMileage: this.autoMileage,
        isPrimaryCurrency : this.isPrimaryCurrency,
      };

      this.itinerary.days[this.selectedIndexDate].transport.push(payload);
      await this.activeService.updateItineraryLockState(withItineraryLockSession({_id: this.itinerary._id, days: this.itinerary.days}, this.sessionId!));
      this.isButtonLoading = false;
      this.modal.close(SETTINGS.ITINERARY_MESSAGE_STATUS.ADD_SUCCESS);
    } catch (e) {
      this.isButtonLoading = false;
      this.modal.close(SETTINGS.ITINERARY_MESSAGE_STATUS.ERROR);
    }
  }

  async getCurrentLocation(): Promise<any> {
    try {
      return await this.googlePlacesService.postTextSearch({
        text: this.country
      });
    } catch (e) {
      this.message.error(this.translocoService.translate('itineraries.transport.messages.countrySearchNotWorking'));
    }
  }

  async editTransport(): Promise<any> {
    this.isButtonLoading = true;
    try {
      const formData = this.transportForm.getRawValue();
      formData.mileage = formData.mileage !== "" ? formData.mileage : 0;
      formData.costPerMileage = formData.costPerMileage !== "" ? formData.costPerMileage : 0;
      formData.driverBata = formData.driverBata !== "" ? formData.driverBata : 0;

      const selectedTransport = this.itinerary.days[this.dayIndex].transport[this.transportIndex];
      const newDayIndex = this.selectedDayIndex ?? this.dayIndex;
      const selectedFrom = this.selectedTransportFrom;
      const selectedTo = this.selectedTransportTo;

      const displayNameFrom = this.selectedTransportFrom
        ? `${this.selectedTransportFrom.structuredFormat?.mainText?.text}`
        : selectedTransport.displayNameFrom;

      const displayNameTo = this.selectedTransportTo
        ? `${this.selectedTransportTo.structuredFormat?.mainText?.text}`
        : selectedTransport.displayNameTo;

      const locationFrom = await this.selectedTransportLocation(selectedFrom?.placeId ?? selectedTransport.from);
      const locationTo = await this.selectedTransportLocation(selectedTo?.placeId ?? selectedTransport.to);

      const updatedTransport = {
        ...formData,
        from: selectedFrom?.placeId ?? selectedTransport.from,
        to: selectedTo?.placeId ?? selectedTransport.to,
        selectedDateIndex: newDayIndex,
        displayNameFrom,
        displayNameTo,
        locationFrom,
        locationTo,
        autoMileage: this.autoMileage,
        isPrimaryCurrency: this.isPrimaryCurrency,
      };

      if (newDayIndex !== this.dayIndex) {
        // Remove from the original day
        this.itinerary.days[this.dayIndex].transport.splice(this.transportIndex, 1);
        // Push to the new day
        this.itinerary.days[newDayIndex].transport.push(updatedTransport);
      } else {
        // Same day — update in place
        this.itinerary.days[this.dayIndex].transport[this.transportIndex] = updatedTransport;
      }

      await this.activeService.updateItineraryLockState(withItineraryLockSession({_id: this.itinerary._id, days: this.itinerary.days}, this.sessionId!));
      this.isButtonLoading = false;
      this.modal.close(SETTINGS.ITINERARY_MESSAGE_STATUS.EDIT_SUCCESS);
    } catch (e) {
      this.isButtonLoading = false;
      this.modal.close(SETTINGS.ITINERARY_MESSAGE_STATUS.ERROR);
    }
  }

  getTimeTaken(): void {
    const payload = {
      destinations: `place_id:${this.selectedTransportTo.placeId}`,
      origins: `place_id:${this.selectedTransportFrom.placeId}`,
    };
    this.googleMatrixService.postDistanceMatrix(payload).subscribe({
      next: value => {
        const {elements} = value.rows[0];
        const converted = this.convertSecondsToHoursAndMinute(elements[0]?.duration?.value || 0);
        this.autoMileage = this.parseOutNumberFromText(elements[0]?.distance?.text);
        if (converted !== null) {
          this.transportForm.patchValue({...converted, mileage: this.autoMileage}, {emitEvent: true});
        }
        this.isBothDestinationSelected.next(false);
      },
      error: error => {
        this.message.error(this.translocoService.translate('itineraries.transport.messages.unableToFetchTimeTaken'));
      },
    });

  }

  convertSecondsToHoursAndMinute(seconds: number): any {
    if (seconds < 0) {
      return null;
    }
    const hours = Math.floor(seconds / 3600);
    const remainingSeconds = seconds % 3600;
    const minutes = Math.floor(remainingSeconds / 60);
    return {hours, minutes};
  }

  convertDistance(value: number, targetUnit: "km" | "miles"): number {
    const conversionRate = 0.621371;

    if (targetUnit === "miles") {
      return Math.round(value * conversionRate * 10) / 10;
    } else{
      return Math.round((value / conversionRate) * 10) / 10;
    }
  }
  parseOutNumberFromText(text: string): number {
    const match: any = text.match(/\d+(\.\d+)?/);
    const value = parseFloat(match[0]);
    return value;
  }

  mileageFormVaildityUpdateForms(bol: boolean): void{
    if (bol){
      this.transportForm.get("mileage")?.disable();
      this.transportForm.get("mileageUnit")?.markAsUntouched();
      this.transportForm.patchValue({ mileage: this.autoMileage, mileageUnit: 'km'} , {emitEvent: true});
      this.transportForm.get("mileage").updateValueAndValidity();
    }else{
      this.transportForm.get("mileage")?.enable();
      this.transportForm.get("mileage").updateValueAndValidity();
    }
  }

  mileageFormVaildityUpdateOnEdit(bol: boolean): void{
    if (bol){
      this.transportForm.get("mileage")?.disable();
      this.transportForm.get("mileageUnit")?.markAsUntouched();
      this.transportForm.get("mileage").updateValueAndValidity();
    }else{
      this.transportForm.get("mileage")?.enable();
      this.transportForm.get('mileage').updateValueAndValidity();
    }
  }
  getTransportConfig(isPatch: boolean): [] {
    const totalPax = this.itinerary.tour.pax.adults + this.itinerary.tour.pax.children;
    const matchingRanges = this.transportConfig.filter(
      ({ lowerBound, upperBound }: any) =>
        totalPax >= lowerBound && totalPax <= upperBound
    );

    const desiredCurrency = this.isPrimaryCurrency
      ? this.itinerary.tour.currency
      : this.itinerary.tour.secondaryCurrency;

    const currencyRanges = matchingRanges.filter( (r: any) => r.currency === desiredCurrency);
    if (isPatch){
      const costPerMileage =
        currencyRanges.length !== 0
          ? currencyRanges[0].costPerMileage
          : 0;

      const transVehicleConfig =  currencyRanges.length !== 0
        ? currencyRanges[0].vehicleType
        : "";

      const vehicleImage = currencyRanges.length !== 0
        ? currencyRanges[0]?.image || ""
        : "";

      this.costPerMileage = costPerMileage;
      this.transportForm.patchValue({ costPerMileage, transVehicleConfig, vehicleImage });
    }

    return currencyRanges;
  }

  getAltConfigs(): [] {
    const totalPax = this.itinerary.tour.pax.adults + this.itinerary.tour.pax.children + 1;
    const matchingRanges = this.transportConfig.filter(
      ({ upperBound }: any) =>
        upperBound > totalPax
    );

    const desiredCurrency = this.isPrimaryCurrency
      ? this.itinerary.tour.currency
      : this.itinerary.tour.secondaryCurrency;

    const currencyRanges = matchingRanges.filter( (r: any) => r.currency === desiredCurrency);

    return currencyRanges;
  }

  addCurrency(): void {
    if (this.itinerary?.tour?.currency) {
      this.currencies.push({label: this.itinerary?.tour?.currency, value: true});
    }

    if (this.itinerary?.tour?.secondaryCurrency !== ""){
      this.currencies.push({label: this.itinerary?.tour?.secondaryCurrency, value: false});
    }
  }

  onClickPopulate(data: any): void {
    this.costPerMileage = data.costPerMileage;
    this.transportForm.patchValue({ transVehicleConfig: data.vehicleType, costPerMileage: data.costPerMileage, vehicleImage: data.image }, {emitEvent: true});
  }
  onClickClear(): void {
    this.costPerMileage = 0;
    this.transportForm.patchValue({ costPerMileage: 0, vehicleImage: '', transVehicleConfig: '' }, {emitEvent: true});
  }
  onChangeCurrency(event: boolean): void{
    this.filteredTransportValues = this.getTransportConfig(false);
  }

  vehicleTypeExistsInConfig(type: string): string{
    const isMatch = this.transportConfig.some( (congfig: any) => congfig.vehicleType === type);
    if (isMatch) {
      return type;
    }
    return "";
  }

  setSelectedIndex(selectedDate: any) {
    this.selectedDayIndex = this.itinerary.days.findIndex(
      (day: any) => day.date === selectedDate
    );
    console.log(this.selectedDayIndex);
  }

  formatter = currencyFormatter;
  parser = amountParser;
}
